import { useAuthStore } from '../store/authStore'

/**
 * Built-in role names that the tenant frontend understands.
 * Backed by entries in identity service `roles` table (per tenant).
 */
export const ROLES = {
  ADMIN: 'admin',
  USER: 'user',
  DRIVER: 'driver',
  VIEWER: 'viewer',
} as const

/**
 * Built-in permission keys. Mirrors `permissions` table in identity DB.
 */
export const PERMS = {
  // Devices
  VIEW_ALL_DEVICES: 'view_all_devices',
  VIEW_ASSIGNED_DEVICES: 'view_assigned_devices',
  MANAGE_DEVICE: 'manage_device',
  DELETE_DEVICE: 'delete_device',
  MANAGE_DEVICE_ASSIGNMENTS: 'manage_device_assignments',
  MANAGE_DEVICE_GROUPS: 'manage_device_groups',
  // Sensors
  VIEW_SENSORS: 'view_sensors',
  MANAGE_SENSORS: 'manage_sensors',
  // Rules & alerts
  MANAGE_RULES: 'manage_rules',
  VIEW_RULES: 'view_rules',
  VIEW_ALERTS: 'view_alerts',
  MANAGE_ALERTS: 'manage_alerts',
  ACKNOWLEDGE_ALERTS: 'acknowledge_alerts',
  ACKNOWLEDGE_ALERT: 'acknowledge_alert',
  // Identity
  MANAGE_USERS: 'manage_users',
  VIEW_USERS: 'view_users',
  MANAGE_ROLES: 'manage_roles',
  // Tenant
  VIEW_TENANTS: 'view_tenants',
  MANAGE_TENANTS: 'manage_tenants',
  // Other
  VIEW_ANALYTICS: 'view_analytics',
  EXPORT_ANALYTICS: 'export_analytics',
  VIEW_TRIPS: 'view_trips',
  VIEW_DASHBOARD: 'view_dashboard',
  VIEW_NOTIFICATIONS: 'view_notifications',
  MANAGE_NOTIFICATIONS: 'manage_notifications',
  MANAGE_NOTIFICATION_ROUTING: 'manage_notification_routing',
  MANAGE_TRUST_CIRCLES: 'manage_trust_circles',
  MANAGE_BILLING: 'manage_billing',
} as const

export type PermissionKey = (typeof PERMS)[keyof typeof PERMS]
export type RoleName = (typeof ROLES)[keyof typeof ROLES]

/**
 * usePermissions returns a small helper API for role/permission checks
 * driven by the values stored in the auth store (populated at login from
 * the JWT claims via /api/v1/auth/me).
 */
export function usePermissions() {
  const user = useAuthStore((s) => s.user)

  const roles: string[] = user?.roles ?? []
  const permissions: string[] = user?.permissions ?? []

  const hasRole = (...wanted: string[]) => wanted.some((r) => roles.includes(r))
  const hasPermission = (...wanted: string[]) =>
    wanted.some((p) => permissions.includes(p))
  const hasAllPermissions = (...wanted: string[]) =>
    wanted.every((p) => permissions.includes(p))

  const isAdmin  = hasRole(ROLES.ADMIN)
  const isUser   = hasRole(ROLES.USER)
  const isDriver = hasRole(ROLES.DRIVER)
  const isViewer = hasRole(ROLES.VIEWER)

  return {
    roles,
    permissions,
    hasRole,
    hasPermission,
    hasAllPermissions,
    isAdmin,
    isUser,
    isDriver,
    isViewer,
  }
}
